import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

/**
 * Composant de calendrier de disponibilité
 * Affiche les dates réservées pour une villa
 * Intègre les règles de réservation (switchover_day, min_stay)
 * Permet la sélection de dates et calcule le tarif
 */
const AvailabilityCalendar = ({ 
  bookedDates = [], 
  loading = false, 
  switchoverDay = '', 
  minStay = null,
  rates = [], // Tarifs de la villa
  villaId = null,
  compact = false, // Mode compact pour modal
  onDateSelect = null // Callback pour passer les dates au parent
}) => {
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [months, setMonths] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);

  // Générer 12 mois à partir du mois actuel
  useEffect(() => {
    const monthsData = [];
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 12; i++) {
      const date = new Date(today);
      date.setMonth(today.getMonth() + i);
      monthsData.push(date);
    }

    setMonths(monthsData);
  }, []);

  // Notifier le parent quand les dates changent (mode compact)
  useEffect(() => {
    if (compact && onDateSelect && selectedStartDate && selectedEndDate) {
      onDateSelect({
        checkIn: selectedStartDate.toISOString().split('T')[0],
        checkOut: selectedEndDate.toISOString().split('T')[0]
      });
    }
  }, [selectedStartDate, selectedEndDate, compact, onDateSelect]);

  // Vérifier si une date est réservée
  const isDateBooked = (date) => {
    if (!bookedDates || bookedDates.length === 0) return false;

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return bookedDates.some(booking => {
      const start = new Date(booking.start);
      const end = new Date(booking.end);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      return checkDate >= start && checkDate <= end;
    });
  };

  // Vérifier si une date est dans le passé
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Vérifier si un jour correspond au switchover_day autorisé
  const isValidArrivalDay = (date) => {
    if (!switchoverDay || switchoverDay === '') return true; // Pas de restriction
    
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dateDay = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const requiredDay = switchoverDay.toLowerCase();
    
    return dayNames[dateDay] === requiredDay;
  };

  // Gérer le clic sur une date
  const handleDateClick = (date) => {
    // Ignorer si réservé ou passé
    if (isDateBooked(date) || isPastDate(date)) return;
    
    // Si pas de date de début ou si on veut recommencer
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Vérifier que c'est un jour d'arrivée valide
      if (!isValidArrivalDay(date)) {
        alert(`Les arrivées sont autorisées uniquement le ${switchoverDay}`);
        return;
      }
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else {
      // Sélectionner la date de fin
      if (date < selectedStartDate) {
        // Si la date est avant la date de début, recommencer
        if (!isValidArrivalDay(date)) {
          alert(`Les arrivées sont autorisées uniquement le ${switchoverDay}`);
          return;
        }
        setSelectedStartDate(date);
        setSelectedEndDate(null);
      } else {
        // Calculer le nombre de nuits
        const nights = Math.ceil((date - selectedStartDate) / (1000 * 60 * 60 * 24));
        
        // Vérifier le séjour minimum
        if (minStay && nights < minStay) {
          alert(`Le séjour minimum est de ${minStay} nuits`);
          return;
        }
        
        setSelectedEndDate(date);
      }
    }
  };

  // Vérifier si une date est dans la plage sélectionnée
  const isInSelectedRange = (date) => {
    if (!selectedStartDate) return false;
    if (!selectedEndDate && !hoveredDate) return false;
    
    const endDate = selectedEndDate || hoveredDate;
    const dateTime = date.getTime();
    const startTime = selectedStartDate.getTime();
    const endTime = endDate.getTime();
    
    return dateTime >= startTime && dateTime <= endTime;
  };

  // Calculer le tarif total
  const calculatePrice = () => {
    if (!selectedStartDate || !selectedEndDate || !rates || rates.length === 0) {
      return null;
    }
    
    const nights = Math.ceil((selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24));
    
    // Trouver le tarif applicable
    // Pour simplifier, on prend le premier tarif qui correspond à la période
    let totalPrice = 0;
    let currentDate = new Date(selectedStartDate);
    
    for (let i = 0; i < nights; i++) {
      const rate = rates.find(r => {
        const start = new Date(r.period_start);
        const end = new Date(r.period_end);
        return currentDate >= start && currentDate <= end;
      });
      
      if (rate) {
        totalPrice += parseFloat(rate.price);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      nights,
      total: totalPrice,
      perNight: totalPrice / nights
    };
  };

  const priceDetails = calculatePrice();

  // Générer les jours d'un mois
  const generateCalendarDays = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Jours vides avant le premier jour du mois
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  // Navigation
  const goToPrevMonth = () => {
    setCurrentMonthIndex(prev => Math.max(0, prev - 1));
  };

  const goToNextMonth = () => {
    const maxIndex = compact ? months.length - 1 : months.length - 3;
    setCurrentMonthIndex(prev => Math.min(maxIndex, prev + 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        <span className="ml-3 text-gray-600">Chargement des disponibilités...</span>
      </div>
    );
  }

  if (months.length === 0) return null;

  // Afficher 1 mois en mode compact, 3 mois sinon
  const monthsToShow = compact ? 1 : 3;
  const visibleMonths = months.slice(currentMonthIndex, currentMonthIndex + monthsToShow);

  return (
    <div className="w-full">
      {/* En-tête avec navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPrevMonth}
          disabled={currentMonthIndex === 0}
          className="hover:bg-amber-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex items-center gap-2 text-gray-700">
          <CalendarIcon className="w-5 h-5 text-amber-600" />
          <span className="font-medium">Disponibilités</span>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={goToNextMonth}
          disabled={currentMonthIndex >= (compact ? months.length - 1 : months.length - 3)}
          className="hover:bg-amber-50"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Grille responsive */}
      <div className={`grid gap-6 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {visibleMonths.map((monthDate, idx) => {
          const days = generateCalendarDays(monthDate);
          
          return (
            <Card key={idx} className="p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
              {/* Nom du mois */}
              <h4 className="text-center font-semibold text-gray-800 mb-4">
                {monthNames[monthDate.getMonth()]} {monthDate.getFullYear()}
              </h4>

              {/* En-têtes des jours */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                  <div
                    key={day}
                    className="text-xs text-center font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Grille des jours */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((date, dayIdx) => {
                  if (!date) {
                    return <div key={`empty-${dayIdx}`} className="aspect-square" />;
                  }

                  const booked = isDateBooked(date);
                  const past = isPastDate(date);
                  const validArrival = isValidArrivalDay(date);
                  const inRange = isInSelectedRange(date);
                  const isStart = selectedStartDate && date.getTime() === selectedStartDate.getTime();
                  const isEnd = selectedEndDate && date.getTime() === selectedEndDate.getTime();
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isToday = date.getTime() === today.getTime();

                  // Logique de priorité pour l'affichage
                  let bgColor, textColor, titleText, cursorStyle, extraClasses = '';
                  
                  if (booked) {
                    bgColor = 'bg-red-100';
                    textColor = 'text-red-700 font-semibold';
                    titleText = 'Réservé';
                    cursorStyle = 'cursor-not-allowed';
                  } else if (past) {
                    bgColor = 'bg-gray-50';
                    textColor = 'text-gray-400';
                    titleText = 'Date passée';
                    cursorStyle = 'cursor-not-allowed';
                  } else {
                    // Disponible
                    cursorStyle = 'cursor-pointer hover:bg-green-200';
                    
                    // Sélection
                    if (isStart || isEnd) {
                      bgColor = 'bg-amber-500';
                      textColor = 'text-white font-bold';
                      titleText = isStart ? 'Date d\'arrivée sélectionnée' : 'Date de départ sélectionnée';
                    } else if (inRange) {
                      bgColor = 'bg-amber-100';
                      textColor = 'text-amber-800';
                      titleText = 'Dans la période sélectionnée';
                    } else {
                      // Disponible - afficher TOUS les jours en vert
                      bgColor = 'bg-green-50';
                      textColor = 'text-green-700';
                      
                      if (validArrival) {
                        // Jour d'arrivée autorisé - bordure spéciale
                        extraClasses = 'ring-2 ring-green-500 font-bold';
                        titleText = `✓ Jour d'arrivée autorisé - Cliquez pour sélectionner`;
                      } else {
                        // Disponible mais pas un jour d'arrivée
                        titleText = `Disponible - Cliquez pour sélectionner (arrivée le ${switchoverDay})`;
                      }
                    }
                  }

                  return (
                    <div
                      key={dayIdx}
                      className={`
                        aspect-square flex items-center justify-center text-sm rounded-md
                        transition-all duration-200
                        ${bgColor} ${textColor} ${cursorStyle} ${extraClasses}
                        ${isToday ? 'ring-2 ring-blue-500' : ''}
                      `}
                      title={titleText}
                      onClick={() => handleDateClick(date)}
                      onMouseEnter={() => selectedStartDate && !selectedEndDate && setHoveredDate(date)}
                      onMouseLeave={() => setHoveredDate(null)}
                    >
                      {date.getDate()}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Légende */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 rounded border border-green-200" />
          <span className="text-gray-600">Disponible</span>
        </div>
        {switchoverDay && switchoverDay !== '' && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 rounded border-2 border-green-500 font-bold" />
            <span className="text-gray-600">Jour d'arrivée possible</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 rounded border border-red-200" />
          <span className="text-gray-600">Réservé</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded border border-gray-200" />
          <span className="text-gray-600">Passé</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-amber-500" />
          <span className="text-gray-600">Aujourd'hui</span>
        </div>
      </div>
      
      {/* Message d'information sur les règles */}
      {switchoverDay && switchoverDay !== '' && !selectedStartDate && (
        <div className="mt-4 text-center text-sm text-gray-600 bg-green-50 border border-green-200 p-4 rounded-lg">
          <p className="mb-2">
            <strong className="text-green-700">ℹ️ Règle d'arrivée :</strong> Les jours avec une <strong>bordure verte épaisse</strong> sont les jours d'arrivée autorisés ({switchoverDay})
            {minStay && minStay > 1 && ` avec un séjour minimum de ${minStay} nuits`}.
          </p>
          <p className="text-xs text-gray-600">
            Cliquez sur un {switchoverDay} pour commencer votre sélection.
          </p>
        </div>
      )}

      {/* Récapitulatif de réservation (masqué en mode compact) */}
      {!compact && selectedStartDate && (
        <div className="mt-6 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl">
          <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-600" />
            Votre sélection
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Date d'arrivée */}
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Arrivée</p>
              <p className="text-lg font-semibold text-gray-800">
                {selectedStartDate.toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>

            {/* Date de départ */}
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Départ</p>
              <p className="text-lg font-semibold text-gray-800">
                {selectedEndDate ? (
                  selectedEndDate.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })
                ) : (
                  <span className="text-amber-600">Sélectionnez une date de départ</span>
                )}
              </p>
            </div>
          </div>

          {/* Détails du tarif */}
          {priceDetails && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-amber-200">
              <h5 className="font-semibold text-gray-800 mb-3">Détails du tarif</h5>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Nombre de nuits</span>
                  <span className="font-medium">{priceDetails.nights} nuit{priceDetails.nights > 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Prix par nuit (moyen)</span>
                  <span className="font-medium">{Math.round(priceDetails.perNight).toLocaleString('fr-FR')} €</span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-800">Total</span>
                  <span className="text-2xl font-bold text-amber-600">
                    {Math.round(priceDetails.total).toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>

              <Button 
                className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => {
                  // TODO: Ouvrir modal de réservation
                  alert('Fonctionnalité de réservation à venir !');
                }}
              >
                Demander une réservation
              </Button>

              <button 
                className="w-full mt-2 text-sm text-gray-600 hover:text-gray-800"
                onClick={() => {
                  setSelectedStartDate(null);
                  setSelectedEndDate(null);
                  setHoveredDate(null);
                }}
              >
                Réinitialiser la sélection
              </button>
            </div>
          )}

          {!selectedEndDate && (
            <p className="text-sm text-center text-gray-600 mt-4">
              Cliquez sur une date pour sélectionner votre départ
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;

